import React, { useState } from 'react'
import { AlertTriangle, X, CheckCircle, Clock, Info, TrendingUp, Eye, ChevronDown, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import type { RiskAlert } from '@/types/risk-assessment'

interface RiskAlertsPanelProps {
  alerts: RiskAlert[]
  onAcknowledge: (alertId: string, notes?: string) => Promise<void>
  onDismiss?: (alertId: string, reason?: string) => Promise<void>
  loading?: boolean
}

export function RiskAlertsPanel({ alerts, onAcknowledge, onDismiss, loading = false }: RiskAlertsPanelProps) {
  const [expandedAlerts, setExpandedAlerts] = useState<Set<string>>(new Set())
  const [acknowledgingAlert, setAcknowledgingAlert] = useState<string | null>(null)
  const [acknowledgmentNotes, setAcknowledmentNotes] = useState<Record<string, string>>({})

  const unacknowledgedAlerts = alerts.filter(alert => !alert.acknowledged)

  if (loading) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <span className="text-blue-800">Loading risk alerts...</span>
        </div>
      </div>
    )
  }

  if (unacknowledgedAlerts.length === 0) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-8">
        <div className="flex items-center space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <span className="text-green-800 font-medium">No active risk alerts</span>
          <span className="text-green-600 text-sm">Your portfolio is within acceptable risk parameters.</span>
        </div>
      </div>
    )
  }

  const toggleExpanded = (alertId: string) => {
    const newExpanded = new Set(expandedAlerts)
    if (expandedAlerts.has(alertId)) {
      newExpanded.delete(alertId)
    } else {
      newExpanded.add(alertId)
    }
    setExpandedAlerts(newExpanded)
  }

  const handleAcknowledge = async (alertId: string) => {
    try {
      setAcknowledgingAlert(alertId)
      const notes = acknowledgmentNotes[alertId]
      await onAcknowledge(alertId, notes)
      // Clear notes after successful acknowledgment
      setAcknowledmentNotes(prev => ({ ...prev, [alertId]: '' }))
    } catch (error) {
      console.error('Failed to acknowledge alert:', error)
    } finally {
      setAcknowledgingAlert(null)
    }
  }

  const getSeverityIcon = (severity: RiskAlert['severity']) => {
    switch (severity) {
      case 'critical':
        return <AlertTriangle className="w-5 h-5 text-red-600" />
      case 'high':
        return <AlertTriangle className="w-5 h-5 text-orange-600" />
      case 'medium':
        return <AlertTriangle className="w-5 h-5 text-yellow-600" />
      case 'low':
        return <AlertTriangle className="w-5 h-5 text-blue-600" />
    }
  }

  const getSeverityBg = (severity: RiskAlert['severity']) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-50 border-red-200'
      case 'high':
        return 'bg-orange-50 border-orange-200'
      case 'medium':
        return 'bg-yellow-50 border-yellow-200'
      case 'low':
        return 'bg-blue-50 border-blue-200'
    }
  }

  const getSeverityText = (severity: RiskAlert['severity']) => {
    switch (severity) {
      case 'critical':
        return 'text-red-800'
      case 'high':
        return 'text-orange-800'
      case 'medium':
        return 'text-yellow-800'
      case 'low':
        return 'text-blue-800'
    }
  }

  return (
    <div className="space-y-3 mb-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Risk Alerts ({unacknowledgedAlerts.length})
        </h3>
        {unacknowledgedAlerts.length > 0 && (
          <span className="text-sm text-gray-500">
            Click alerts to expand details
          </span>
        )}
      </div>

      {unacknowledgedAlerts.map((alert) => {
        const isExpanded = expandedAlerts.has(alert.alert_id)
        const isAcknowledging = acknowledgingAlert === alert.alert_id

        return (
          <div
            key={alert.alert_id}
            className={`border rounded-lg transition-all duration-200 ${getSeverityBg(alert.severity)} ${
              isExpanded ? 'shadow-md' : 'shadow-sm hover:shadow-md'
            }`}
          >
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  {getSeverityIcon(alert.severity)}
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <button
                        onClick={() => toggleExpanded(alert.alert_id)}
                        className="flex items-center space-x-2 hover:bg-white/50 rounded px-1 py-0.5 transition-colors"
                      >
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-500" />
                        )}
                        <h4 className={`font-semibold ${getSeverityText(alert.severity)}`}>
                          {alert.title}
                        </h4>
                      </button>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        alert.severity === 'critical' ? 'bg-red-100 text-red-700' :
                        alert.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                        alert.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {alert.severity.toUpperCase()}
                      </span>
                      {alert.risk_score && (
                        <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                          Risk Score: {alert.risk_score}
                        </span>
                      )}
                    </div>

                    <p className={`text-sm ${getSeverityText(alert.severity)} mb-2`}>
                      {alert.description}
                    </p>

                    {/* Basic info always shown */}
                    <div className="flex items-center space-x-4 text-xs text-gray-600 mb-2">
                      <div className="flex items-center space-x-1">
                        <Clock className="w-3 h-3" />
                        <span>{new Date(alert.created_at).toLocaleDateString()}</span>
                      </div>
                      {alert.affected_properties.length > 0 && (
                        <div className="flex items-center space-x-1">
                          <Eye className="w-3 h-3" />
                          <span>
                            {alert.affected_properties.length} {alert.affected_properties.length === 1 ? 'property' : 'properties'}
                          </span>
                        </div>
                      )}
                      {alert.confidence_level && (
                        <div className="flex items-center space-x-1">
                          <TrendingUp className="w-3 h-3" />
                          <span>Confidence: {alert.confidence_level}</span>
                        </div>
                      )}
                    </div>

                    {/* Expanded details */}
                    {isExpanded && (
                      <div className="mt-3 space-y-3 border-t pt-3">
                        <div className={`text-sm ${getSeverityText(alert.severity)}`}>
                          <strong>Recommendation:</strong> {alert.recommendation}
                        </div>

                        {alert.threshold_breached && (
                          <div className="bg-white/50 rounded p-2 text-xs">
                            <strong>Threshold Breached:</strong>
                            <div className="mt-1">
                              {alert.threshold_breached.metric}: {alert.threshold_breached.current_value}
                              {alert.threshold_breached.unit} (threshold: {alert.threshold_breached.threshold_value}
                              {alert.threshold_breached.unit})
                            </div>
                          </div>
                        )}

                        {alert.related_alerts && alert.related_alerts.length > 0 && (
                          <div className="text-xs text-gray-600">
                            <strong>Related alerts:</strong> {alert.related_alerts.length}
                          </div>
                        )}

                        {alert.actions_taken && alert.actions_taken.length > 0 && (
                          <div className="text-xs">
                            <strong>Actions taken:</strong>
                            <ul className="mt-1 space-y-1">
                              {alert.actions_taken.map((action, index) => (
                                <li key={index} className="flex justify-between">
                                  <span>{action.action}</span>
                                  <span className="text-gray-500">
                                    {new Date(action.timestamp).toLocaleDateString()}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Acknowledgment notes input */}
                        <div className="bg-white/50 rounded p-3">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Acknowledgment Notes (optional)
                          </label>
                          <textarea
                            value={acknowledgmentNotes[alert.alert_id] || ''}
                            onChange={(e) => setAcknowledmentNotes(prev => ({
                              ...prev,
                              [alert.alert_id]: e.target.value
                            }))}
                            placeholder="Add notes about how you're addressing this alert..."
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded resize-none"
                            rows={2}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-2 ml-3">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handleAcknowledge(alert.alert_id)}
                    disabled={isAcknowledging}
                    className="flex items-center gap-1"
                  >
                    {isAcknowledging ? (
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
                    ) : (
                      <CheckCircle className="w-3 h-3" />
                    )}
                    {isAcknowledging ? 'Processing...' : 'Acknowledge'}
                  </Button>
                  {onDismiss && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => onDismiss(alert.alert_id)}
                      className="flex items-center gap-1 text-gray-500 hover:text-gray-700"
                    >
                      <X className="w-3 h-3" />
                      Dismiss
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}